import type { HierarchyTreeNode } from "./agency-hierarchy";

/**
 * Back-office intelligence for the hierarchy chart: which branches are carrying the
 * business, which are dead weight, and where the risk sits.
 *
 * DELIBERATELY DETERMINISTIC. Every finding here is arithmetic over data already on the
 * client — no model call, no latency, no per-view token cost, and the same input always
 * gives the same answer. "This agency has 6 agents and $0 production" is a fact, not an
 * opinion, and an LLM adds nothing to it except a chance to be wrong.
 *
 * InsuraBot's job comes AFTER: these findings are handed to it for a narrative summary
 * when the user asks, so the model explains conclusions rather than computing them.
 */

export type InsightSeverity = "critical" | "warning" | "info";

export interface Insight {
  id: string;
  severity: InsightSeverity;
  /** Short label for the card badge. */
  tag: string;
  /** One-sentence finding, phrased for an owner not an engineer. */
  message: string;
  agencyId: string;
  agencyName: string;
}

export interface NodeFlags {
  /** Agents but no production at all in the window — the costliest pattern. */
  deadWeight: boolean;
  /** Nobody owns this agency, so nobody is accountable for it. */
  noOwner: boolean;
  /** Production is present but far below what its headcount implies. */
  underPerforming: boolean;
  /** Carries a disproportionate share — a single point of failure. */
  concentrationRisk: boolean;
  /** Most of the roster hasn't logged in for 30+ days. */
  dormantRoster: boolean;
  /** Production per agent, for ranking. 0 when there are no agents. */
  perAgent: number;
}

const money = (n: number) =>
  n >= 1000 ? `$${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)}k` : `$${Math.round(n)}`;

/**
 * Flatten the tree once. Callers need both per-node flags and a ranked list, and walking
 * twice on every render for a 30-agency org is wasteful.
 */
function flatten(nodes: HierarchyTreeNode[], out: HierarchyTreeNode[] = []) {
  for (const n of nodes) {
    out.push(n);
    flatten(n.children, out);
  }
  return out;
}

export function analyseHierarchy(tree: HierarchyTreeNode[]): {
  flagsById: Map<string, NodeFlags>;
  insights: Insight[];
  /** Production per agent across the org — the yardstick everything is measured against. */
  orgPerAgent: number;
} {
  const all = flatten(tree);
  const totalProduction = all.reduce((n, a) => n + a.production, 0);
  const totalAgents = all.reduce((n, a) => n + a.agentCount, 0);
  const orgPerAgent = totalAgents > 0 ? totalProduction / totalAgents : 0;

  const flagsById = new Map<string, NodeFlags>();
  const insights: Insight[] = [];

  for (const node of all) {
    const perAgent = node.agentCount > 0 ? node.production / node.agentCount : 0;

    // Only judge agencies with enough people to be meaningful — a 1-agent agency having
    // a quiet month is noise, not a finding.
    const meaningful = node.agentCount >= 2;
    const deadWeight = meaningful && node.production === 0;
    // "Half the org average" is the threshold: far enough below to be a real signal,
    // not so tight that normal variance trips it.
    const underPerforming =
      meaningful && !deadWeight && orgPerAgent > 0 && perAgent < orgPerAgent * 0.5;
    const noOwner = !node.ownerName;
    const concentrationRisk =
      totalProduction > 0 && node.depth > 0 && node.totalProduction / totalProduction > 0.5;

    // Activity, not premium: half or more of the roster has gone dark. Judged only on
    // agencies with enough people for the ratio to mean anything — 1 of 1 dormant is a
    // single agent on holiday, not a failing branch.
    const dormantRoster = meaningful && node.staleAgents / node.agentCount >= 0.5;

    flagsById.set(node.id, {
      deadWeight,
      noOwner,
      underPerforming,
      concentrationRisk,
      dormantRoster,
      perAgent,
    });

    if (deadWeight) {
      insights.push({
        id: `dead-${node.id}`,
        severity: "critical",
        tag: "No production",
        message: `${node.name} has ${node.agentCount} agents and zero production this period.`,
        agencyId: node.id,
        agencyName: node.name,
      });
    } else if (underPerforming) {
      insights.push({
        id: `under-${node.id}`,
        severity: "warning",
        tag: "Below average",
        message: `${node.name} is producing ${money(perAgent)} per agent — under half the org average of ${money(orgPerAgent)}.`,
        agencyId: node.id,
        agencyName: node.name,
      });
    }

    if (concentrationRisk) {
      insights.push({
        id: `conc-${node.id}`,
        severity: "warning",
        tag: "Concentration",
        message: `${node.name} carries ${((node.totalProduction / totalProduction) * 100).toFixed(0)}% of all production — a single point of failure.`,
        agencyId: node.id,
        agencyName: node.name,
      });
    }

    if (dormantRoster) {
      insights.push({
        id: `dormant-${node.id}`,
        // Critical when nothing is being produced either — that combination is a branch
        // that has effectively stopped, not one having a slow month.
        severity: deadWeight ? "critical" : "warning",
        tag: "Inactive",
        message: `${node.name} has ${node.staleAgents} of ${node.agentCount} agents with no login in 30+ days.`,
        agencyId: node.id,
        agencyName: node.name,
      });
    }

    if (noOwner && node.agentCount > 0) {
      insights.push({
        id: `noowner-${node.id}`,
        severity: "info",
        tag: "No owner",
        message: `${node.name} has ${node.agentCount} agents but no assigned owner.`,
        agencyId: node.id,
        agencyName: node.name,
      });
    }
  }

  const rank: Record<InsightSeverity, number> = { critical: 0, warning: 1, info: 2 };
  insights.sort((a, b) => rank[a.severity] - rank[b.severity]);

  return { flagsById, insights, orgPerAgent };
}

/**
 * The findings as a compact brief for InsuraBot. Kept short on purpose — the model is
 * being asked to interpret and prioritise, not to re-derive numbers it can't verify.
 */
export function buildInsightPrompt(
  insights: Insight[],
  orgPerAgent: number,
  windowLabel: string,
): string {
  const lines = insights.slice(0, 15).map((i) => `- [${i.severity}] ${i.message}`);
  return [
    `You are reviewing an insurance agency's downline for the period: ${windowLabel}.`,
    `Org-wide production per agent: ${money(orgPerAgent)}.`,
    "",
    "Findings:",
    ...lines,
    "",
    "In 3-4 short sentences: what should the owner do first, and why? Be direct and",
    "specific. Do not invent numbers beyond those given.",
  ].join("\n");
}
